<template>
    <div class="container">
        <div class="input-group my-3 rounded-pill">
            <input type="text" class="form-control" :placeholder="$t('task.searchPlaceholder')" v-model="searchTerm"
                @keyup.enter.prevent="onSearchSubmit" />
            <button class="input-group-text bg-white" type="button" @click="onSearchSubmit">
                <i class="fa-solid fa-magnifying-glass"></i>
            </button>
        </div>

        <HomeTaskCount
            v-for="item in items"
            :key="item.id"
            :taskCount="item.count"
            :headline="item.realestate_name"
            :description="item.project_name"
            :link="{
                name: targetRouteName,
                query: {
                    project_id: item.project_id,
                    priority_id: priorityId,
                    search: appliedSearch || undefined,
                }
            }"
            :numberClass="getNumberClass(item)"
            textClass="smaller"
        />

        <!-- Only stands in for the cards on the very first load. A refetch after a
             search keeps the old cards on screen instead of blinking to a spinner. -->
        <div v-if="loading && items.length === 0" class="text-center text-body-tertiary my-5">
            <span class="spinner-border spinner-border-sm me-2"></span>{{ $t('common.loading') }}
        </div>

        <div v-else-if="items.length === 0" class="text-center text-body-tertiary my-5">
            <template v-if="appliedSearch">
                <i class="fa-solid fa-magnifying-glass fa-2x d-block mb-3"></i>
                <div class="fst-italic small">{{ $t('task.noSearchResults', { search: appliedSearch }) }}</div>
                <button type="button" class="btn btn-link btn-sm" @click="clearSearch">
                    {{ $t('common.clearSearch') }}
                </button>
            </template>
            <template v-else>
                <i class="fa-regular fa-folder-open fa-2x d-block mb-3"></i>
                <div class="fst-italic small">{{ $t('task.noGroupedTasks') }}</div>
            </template>
        </div>
    </div>
</template>

<script>
import axios from '@/services/axios';
import HomeTaskCount from '../components/HomeTaskCount.vue';

export default {
    name: 'TasksGroupedListView',
    components: { HomeTaskCount },

    props: {
        // what used to be hardcoded &priority_id=3
        priorityId: {
            type: Number
        },
        // where the click on a card should go (TasksUnfinished / TasksOrder / ...)
        targetRouteName: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            items: [],
            searchTerm: '',        // bound to input; mirrors ?search=
            loading: false,        // gates the empty state, so it cannot flash pre-fetch
        };
    },

    computed: {
        currentUser() {
            const user = localStorage.getItem('user');
            return user ? JSON.parse(user) : null;
        },
        // The term the counts on screen were actually fetched with. Kept apart from
        // searchTerm, which can hold a half-typed one -- handing that to the task list
        // would open it on a different query than the count the user clicked.
        appliedSearch() {
            return (this.$route.query.search ?? '').toString();
        },
    },

    watch: {
        // The URL is the source of truth, as in TasksListComponent: onSearchSubmit
        // only pushes, and the fetch happens here. Also covers back/forward.
        '$route.query.search'(newVal) {
            const s = (newVal ?? '').toString();
            if (this.searchTerm !== s) this.searchTerm = s;
            this.fetchTasks();
        },
    },

    async mounted() {
        localStorage.removeItem('taskListFilterKeys');
        document.body.style.backgroundColor = 'white';

        // Seeded here rather than by an immediate watcher, which would fetch a second
        // time alongside the call below.
        this.searchTerm = this.appliedSearch;

        await this.fetchTasks();
    },

    beforeUnmount() {
        document.body.style.backgroundColor = '';
    },

    methods: {
        onSearchSubmit() {
            const term = (this.searchTerm || '').trim();
            const q = { ...this.$route.query };
            if (term) q.search = term; else delete q.search;
            this.$router.push({ query: q });
            // Do NOT call fetch here; the watcher will fetch once.
        },

        clearSearch() {
            this.searchTerm = '';
            this.onSearchSubmit();
        },

        async fetchTasks() {
            if (!this.currentUser?.id) return;

            this.loading = true;
            try {
                const { data } = await axios.get('tasks/summary-grouped', {
                    params: {
                        users: this.currentUser.id,
                        priority_id: this.priorityId,
                        search: this.appliedSearch || undefined,
                    }
                });

                this.items = data.data;
            } catch (error) {
                console.error('Error fetching tasks:', error);
            } finally {
                this.loading = false;
            }
        },

        getNumberClass(item) {
            if (item.overdue > 0 || item.agreed > 0) {
                return 'div-red';
            } else if (item.in_progress > 0) {
                return 'div-yellow';
            } else {
                return 'div-green';
            }
        }
    }
};
</script>
